import {
    Alert,
    Box,
    CircularProgress,
    Slider,
    Stack,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material'

import React, { useEffect, useMemo, useState, FC } from 'react'
import { sendAction } from '@/services/sensors.service'
import { getEquipments } from '@/services/realtime.service'
import { insertAction } from '@/utils/realtime.utils'
import { useRealtime } from '../useRealtime'
import { useTranslation } from 'react-i18next'
import uniq from 'lodash/uniq'
import { useQuery } from '@tanstack/react-query'
import { Group } from '../types'
import {
    getSharedSetpointRange,
    getZoneCommandCapabilityFromEquipments,
    GetEquipmentsResponse,
    DEFAULT_MAX_SETTEMP,
    DEFAULT_MIN_SETTEMP,
} from './SelectedZonesTelecommand.utils'

const marks = [
    {
        value: 20,
        label: '20°C',
    },
    {
        value: 25,
        label: '25°C',
    },
]

function formatValue(value: number) {
    return `${value}°C`
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const useGroupTelecommand = (item: Group) => {
    const { site } = useRealtime()
    const objectIds = useMemo(() => uniq(item?.objects || []), [item?.objects])

    const capabilitiesQuery = useQuery({
        queryKey: ['groupTelecommand', site.hash, item.hash],
        enabled: objectIds.length > 0,
        staleTime: 60 * 1000,
        queryFn: async () =>
            Promise.all(
                objectIds.map(async (objectId) => {
                    const response = (await getEquipments({
                        hash: site.hash,
                        objectId,
                    })) as GetEquipmentsResponse
                    return getZoneCommandCapabilityFromEquipments(objectId, response?.equipments)
                })
            ),
    })

    const capabilities = capabilitiesQuery.data || []
    const driveActuatorId = capabilities.find((capability) => capability.driveActuatorId)?.driveActuatorId
    const setTempActuatorId = capabilities.find((capability) => capability.setTempActuatorId)?.setTempActuatorId
    const sharedSetpointRange = getSharedSetpointRange(capabilities)

    return {
        isLoading: capabilitiesQuery.isLoading,
        isError: capabilitiesQuery.isError,
        driveActuatorId,
        setTempActuatorId: sharedSetpointRange ? setTempActuatorId : undefined,
        setTempMin: sharedSetpointRange?.min ?? DEFAULT_MIN_SETTEMP,
        setTempMax: sharedSetpointRange?.max ?? DEFAULT_MAX_SETTEMP,
        site,
    }
}

interface GroupTelecommandProps {
    item: Group
    open: boolean
    onClose: () => void
}

const GroupTelecommandForm: FC<GroupTelecommandProps> = ({ item, onClose }) => {
    const { t } = useTranslation('site', { keyPrefix: 'realtime.groupTelecommand' })
    const { isLoading, isError, driveActuatorId, setTempActuatorId, setTempMin, setTempMax, site } =
        useGroupTelecommand(item)
    const [drive, setDrive] = React.useState<boolean>(false)
    const [setTemp, setSetTemp] = React.useState<number>(20)
    const [sending, setSending] = useState(false)
    const [submitError, setSubmitError] = useState(false)

    useEffect(() => {
        setSetTemp((current) => clamp(current, setTempMin, setTempMax))
    }, [setTempMin, setTempMax])

    const handleChangeSetTemp = (event: Event, newValue: number | number[]) => {
        setSetTemp(Array.isArray(newValue) ? newValue[0] : newValue)
    }

    const closeButton = (
        <DialogActions sx={{ mt: 2 }}>
            <Button onClick={onClose} color="inherit">
                {t('actions.close')}
            </Button>
        </DialogActions>
    )

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
            </Box>
        )
    }
    // No silent no-op: when the group carries no commandable actuator (or the
    // lookup failed), say so instead of rendering an empty dialog.
    if (isError || (!driveActuatorId && !setTempActuatorId)) {
        return (
            <>
                <Alert severity="warning">{isError ? t('errors.capabilities') : t('warnings.noCommandable')}</Alert>
                {closeButton}
            </>
        )
    }

    const submit = async () => {
        setSending(true)
        setSubmitError(false)
        const modifications = []
        if (driveActuatorId) {
            modifications.push({
                id: driveActuatorId,
                value: drive ? 1 : 0,
                title: '',
                actuatorId: driveActuatorId,
            })
        }
        if (setTempActuatorId) {
            modifications.push({
                id: setTempActuatorId,
                value: setTemp,
                title: '',
                actuatorId: setTempActuatorId,
            })
        }
        try {
            if (modifications.length !== 0) {
                await sendAction({
                    groupHashes: [item.hash],
                    modifications,
                    hash: site?.hash,
                    fromDashboard: true,
                })
                insertAction(modifications)
            }
            onClose()
        } catch (error) {
            setSubmitError(true)
        } finally {
            setSending(false)
        }
    }

    return (
        <>
            <Stack spacing={2}>
                {driveActuatorId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography>{t('fields.state')}</Typography>
                        <ToggleButtonGroup
                            value={drive}
                            exclusive
                            onChange={(event, newValue: boolean | null) => {
                                if (newValue != null) {
                                    setDrive(newValue)
                                }
                            }}
                            color="primary"
                        >
                            <ToggleButton value={true}>ON</ToggleButton>
                            <ToggleButton value={false}>OFF</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}
                {setTempActuatorId && (
                    <Box>
                        <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                            {t('fields.setpointTemperature')}
                        </Typography>
                        <Box sx={{ px: 2 }}>
                            <Slider
                                aria-label="Custom marks"
                                onChange={handleChangeSetTemp}
                                getAriaValueText={formatValue}
                                step={1}
                                value={setTemp}
                                valueLabelDisplay="auto"
                                marks={[
                                    { value: setTempMin, label: `${setTempMin}°C` },
                                    ...marks.filter((mark) => mark.value > setTempMin && mark.value < setTempMax),
                                    { value: setTempMax, label: `${setTempMax}°C` },
                                ]}
                                min={setTempMin}
                                max={setTempMax}
                            />
                        </Box>
                    </Box>
                )}
                <Alert severity="warning">{t('warning')}</Alert>
                {submitError && <Alert severity="error">{t('errors.submit')}</Alert>}
            </Stack>
            <DialogActions sx={{ mt: 2 }}>
                <Button onClick={onClose} color="inherit" disabled={sending}>
                    {t('actions.cancel')}
                </Button>
                <Button
                    variant="contained"
                    disabled={sending}
                    onClick={submit}
                    startIcon={sending ? <CircularProgress color="inherit" size={14} /> : undefined}
                >
                    {t('actions.save')}
                </Button>
            </DialogActions>
        </>
    )
}
export const GroupTelecommand: FC<GroupTelecommandProps> = (props) => {
    const { t } = useTranslation('site', { keyPrefix: 'realtime.groupTelecommand' })
    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="md">
            <DialogTitle>{t('title', { group: props.item.name })}</DialogTitle>
            <DialogContent>
                <GroupTelecommandForm {...props} />
            </DialogContent>
        </Dialog>
    )
}
